import { DataPrivacyClassification } from "./DataCategoriesStep/DataCategoriesStep.types";
import { ProcessingActivitySecurityMeasureModel } from "./dataSubjectStep/DataSubjectStep.types";
import { Guid } from "../common/guid";
import { backgroundWaves } from "../common/components/dotLegalPaper/DotLegalPaper";
import { LevelOfProtectionType } from "../legalEntity/addLegalEntityDialog/AddLegalEntityDialog.types";
import { ProcessingActivityStatus } from "./processingActivities/ProcessingActivities.types";
import { DocumentStatus } from "../documents/Documents.types";
import { Color } from "@dotlegal/dotlegal-ui-components";

export interface SelectableItem {
    name: string;
    id: string;
    description?: string;
}

export interface SelectableTransferBasisItem extends SelectableItem {
    showIfCountryCodeIs?: string;
}

export interface SelectableColoredItem {
    name: string;
    id: string;
    color: Color | undefined;
}

export interface SelectableAgreement {
    name: string;
    id: string;
    type: "contract" | "document" | "yesNo";
    status?: DocumentStatus;
    contractOrDocumentType: string;
}

export interface SelectableDocumentItem {
    name: string;
    id: string;
    legalEntityId: string;
    fileDownloadName: string;
    link: string;
    status?: DocumentStatus;
    documentTypeName: string;
}

export interface SelectableProcessingActivityItem {
    id: string;
    name: string;
    description: string;
}

export interface SelectableSecurityMeasure {
    name: string;
    description: string;
    id: string;
    isInherited: boolean;
    isMet: boolean;
    isGlobal: boolean;
    derivedFromSystems: Array<string>;
}

export interface SelectableDataCategoryItem {
    name: string;
    id: string;
    description: string;
    classification: DataPrivacyClassification;
    dataTypeIds: Array<string>;
}

export interface SelectableLegalUnitsItem {
    name: string;
    id: string;
    levelOfProtection: LevelOfProtectionType;
    isCustomerOwned: boolean;
    isTIAUnnecessary: boolean;
    isTransferBasisUnnecessary: boolean;
    countryCode: string;
    transferBasisId?: string;
}

export interface StepperChild {
    childElement: React.ReactNode;
    graphicType?: StepperGraphicType;
}

export interface StepperGraphicType {
    wave?: backgroundWaves;
    graphic?: "stepOne" | "stepTwo" | "stepFive" | "stepEight" | "systemInfomation" | "systemEdit";
    position?: "left" | "right";
}

export interface CreateProcessingActivityViewModel {
    name: string;
    industryAreas: Array<string>;
    mostPopular: boolean;
    processingActivityStatus: ProcessingActivityStatus;
}

export class CreateProcessingActivityModel {
    name: string;
    industryAreas: Array<ProcessingActivityIndustryAreaEnum>;
    mostPopular: boolean;
    processingActivityStatus: ProcessingActivityStatus;

    constructor(
        name: string,
        industryArea: Array<ProcessingActivityIndustryAreaEnum>,
        mostPopular: boolean,
        processingActivityStatus: ProcessingActivityStatus
    ) {
        this.name = name;
        this.industryAreas = industryArea;
        this.mostPopular = mostPopular;
        this.processingActivityStatus = processingActivityStatus;
    }
}

export class ProcessingActivityModel {
    id: string;
    name: string | undefined;
    description: string | undefined;
    purposes: Array<string> = [];
    dataSubjects: Array<string> = [];
    subjects?: NumberOfDataSubjectsEnum;
    processors?: NumberOfProcessorsEnum;
    noOfSubjects?: number;
    noOfProcessors?: number;
    securityMeasures: Array<ProcessingActivitySecurityMeasureModel> = [];
    legalEntities: Array<string> = [];
    owner: string | null;
    processingAreas: Array<string> = [];
    systems: Array<ProcessingActivitySystem> = [];
    dataCategories: Array<DataCategoryDataTypes> = [];
    legalBasesViewModel!: ProcessingActivityLegalBases;
    dataSubjectDataCategories: Array<DataSubjectDataCategory> = [];
    dataProcessors: Array<DataProcessor> = [];
    disclosures: Array<Disclosure> = [];
    sources: Array<Source> = [];
    jointDataControllers: Array<JointDataController> = [];
    policies: Array<string> = [];
    disableSystems: boolean = false;
    disableDataProcessors: boolean = false;
    disableDisclosures: boolean = false;
    disableSources: boolean = false;
    disableDataControllers: boolean = false;
    disableJointDataControllers: boolean = false;
    hasDataProcessors: boolean = false;
    hasSources: boolean = false;
    hasDisclosures: boolean = false;
    hasDataControllers: boolean = false;
    hasJointDataControllers: boolean = false;
    status: ProcessingActivityStatus = ProcessingActivityStatus.Active;
    sharedResponsibles: Array<string> = [];
    hasMultipleGroupCompanies: boolean = false;
    dataSources: Array<DataSourceEnum> = [];

    constructor() {
        this.owner = "";
        this.id = "";
    }
}

export interface ProcessingActivityUpdateModel {
    id: string;
    name: string | undefined;
    description: string | undefined;
    purposes: Array<string>;
    dataSubjects: Array<string>;
    subjects?: number;
    processors?: number;
    noOfSubjects?: number;
    noOfProcessors?: number;
    securityMeasures: Array<ProcessingActivitySecurityMeasureModel>;
    owner: string | null;
    processingAreas: Array<string>;
    policies: Array<string>;
    disableSystems: boolean;
    disableDataProcessors: boolean;
    disableDisclosures: boolean;
    disableSources: boolean;
    disableDataControllers: boolean;
    disableJointDataControllers: boolean;
    sharedResponsibles: Array<string>;
    dataSources: Array<DataSourceEnum>;
}

export class DataSubjectDataCategory {
    dataCategoryId: string;
    dataSubjectId: string | null;
    dataTypeIds: Array<string>;

    constructor(dataCategoryId: string, dataSubjectId: string | null, dataTypeIds: Array<string>) {
        this.dataCategoryId = dataCategoryId;
        this.dataSubjectId = dataSubjectId;
        this.dataTypeIds = dataTypeIds;
    }
}

export interface DataCategoryDataTypes {
    dataCategoryId: string;
    dataTypeIds: Array<string>;
    canSpecifyDataTypesForAllDataSubjects: boolean;
}

export interface DataCategoryDataTypesSaveModel {
    dataCategoryId: string;
    dataTypeIds: Array<string>;
}

export interface DataCategoryDataTypesStepModel {
    dataCategoryId: string;
    dataTypeIds: Array<string>;
}

export class DataProcessor {
    id: string;
    name?: string;
    description?: string;
    systemId?: string;
    dataProcessor?: string | null;
    dataProcessAgreements: Array<string> = [];
    hasDataProcessorAgreement: boolean = false;
    containsNotAccessibleDocuments: boolean = false;
    responsible?: string | null;
    dataCategories: Array<DataCategoryDataTypes> = [];
    legalEntities: Array<string> = [];
    subProcessors: Array<SubProcessor> = [];
    isSystemGenerated: boolean;
    isGeneratedFromGlobalSystem: boolean;
    transferBasisId?: string | null;
    readOnly: boolean = false;
    createdAsGroupEntityIsDataProcessor: boolean = false;
    hasDataProcessorAccess: boolean = false;
    hasDataControllerAccess: boolean = false;
    hasTransferImpactAssessment: boolean = false;
    transferImpactAssessmentId?: string | null;
    containsNotAccessibleTia: boolean = false;
    dataStorageLocations: Array<string> = [];
    supportLocations: Array<string> = [];

    constructor() {
        this.isSystemGenerated = false;
        this.isGeneratedFromGlobalSystem = false;
        this.id = Guid.newGuid();
    }
}

export class SubProcessor {
    id?: string;
    processorId?: string | null;
    subProcessHasAgreement: boolean = false;
    transferBasisId?: string | null;
    dataCategories: Array<DataCategoryDataTypes> = [];
    hasTransferImpactAssessment: boolean = false;
    transferImpactAssessmentId?: string | null;
    containsNotAccessibleTia: boolean = false;
    dataStorageLocations: Array<string> = [];
    supportLocations: Array<string> = [];
    isLink: boolean = false;
    link?: string;
    note?: string;
}

export class Disclosure {
    id?: string;
    name?: string;
    description?: string;
    legalUnitId?: string;
    transferAgreements: Array<string> = [];
    hasDataProcessingAgreement: boolean = false;
    containsNotAccessibleDocuments: boolean = false;
    responsible?: string;
    dataCategories: Array<DisclosureDataCategory> = [];
    legalEntities: Array<string> = [];
    transferBasis?: string | null;
    readOnly: boolean = false;
}

export class DisclosureDataCategory {
    id?: string;
    dataCategoryId: string;
    dataTypeIds: Array<string>;
    directiveId?: string;
    doubleLegalBasisId?: string;
    nationalLawId?: string;
    additionalDetails?: string;

    constructor(dataCategoryId: string, dataTypeIds: Array<string>) {
        this.dataCategoryId = dataCategoryId;
        this.dataTypeIds = dataTypeIds;
    }
}

export class JointDataController {
    id?: string;
    name?: string;
    description?: string;
    dataControllerId?: string;
    hasAgreement: boolean = false;
    containsNotAccessibleDocuments: boolean = false;
    responsible?: string;
    dataCategories: Array<DataCategoryDataTypes> = [];
    legalEntities: Array<string> = [];
    readOnly: boolean = false;
    documents: Array<string> = [];
}

export class Source {
    id?: string;
    name?: string;
    description?: string;
    dataControllerId?: string;
    agreementIds: Array<string> = [];
    hasDataProcessingAgreement: boolean = false;
    containsNotAccessibleDocuments: boolean = false;
    responsible?: string;
    dataCategories: Array<DataCategoryDataTypes> = [];
    groupEntities: Array<string> = [];
    readOnly: boolean = false;
    generatedFromTransferId?: string;
}

export class ProcessingActivityLegalBases {
    allLegalBases: Array<LegalBasisDataCategoryViewModel> = [];
    purposeSpecificLegalBases: Array<ProcessingActivityPurposeSpecificLegalBasis> = [];

    constructor() {
        this.allLegalBases = [];
        this.purposeSpecificLegalBases = [];
    }
}

export class LegalBasisBaseClass {
    dataCategoryId: string;
    directiveId?: string;
    doubleLegalBasisId?: string;
    nationalLawId?: string;
    balanceOfInterest?: string;
    deletionPeriodId?: string;
    deletionPeriodReason?: string;

    constructor(dataCategoryId: string) {
        this.dataCategoryId = dataCategoryId;
    }
}

export class LegalBasisDataCategoryViewModel extends LegalBasisBaseClass {
    canEdit: boolean | undefined;
}

export class ProcessingActivityPurposeSpecificLegalBasis extends LegalBasisBaseClass {
    purposeId: string | undefined;
}

export interface ProcessingActivitySystem {
    processingActivitySystemId: string;
    systemId: string;
    dataCategories: Array<DataCategoryDataTypes>;
    legalEntityIds: Array<string>;
    subDataProcessors: Array<ProcessingActivitySystemSubDataProcessor>;
    generateSharings: boolean;
}

export interface ProcessingActivitySystemSubDataProcessor {
    systemSubDataProcessorId: string;
    dataCategories: Array<DataCategoryDataTypes>;
}

export interface ProcessingActivityValidationViewModel {
    id: string;
    validatedSteps: Array<StepTypeEnum>;
    isAllStepsValid: boolean;
}

export enum StepTypeEnum {
    masterData,
    dataSubject,
    association,
    dataCategories,
    legalBases,
    systems,
    sharings,
    documentation,
    dataprocessor,
    disclosure,
    sources,
    systemMaster,
    systemAffiliation,
    systemDataProcessor,
    systemSecurity,
    systemDocumentation,
    dataController,
    policies,
    jointDataControllers,
    systemAI,
}

export enum ProcessingActivityIndustryAreaEnum {
    Utility = 1,
    MediaAndCommunication = 2,
    Logistics = 3,
    Retail = 4,
    ECommerce = 5,
    Medicinal = 6,
    MemberOrganizations = 7,
    Municipalities = 8,
    Production = 9,
    Finance = 10,
    Business = 11,
}

export enum DataSourceEnum {
    TheDataSubject = 1,
    ExternalDataSource = 2,
}

export enum NumberOfProcessorsEnum {
    OneToTen = 1,
    ElevenToFifty = 2,
    FiftyOneToOneHundred = 3,
    OneHundredAndOneToOneThousand = 4,
    OneThousandAndOneToInfinite = 5,
    UserEnteredNumberOfProcessors = 6,
}

export enum NumberOfDataSubjectsEnum {
    OneToOneHundred = 1,
    OneHundredAndOneToOneThousand = 2,
    OneThousandAndOneToTenThousand = 3,
    TenThousandAndOneToOneHundredThousand = 4,
    OneHundredThousandAndOneToOneMillion = 5,
    OneMillionAndOneToInfinite = 6,
    UserEnteredNumberOfSubjects = 7,
}

export interface DataCategoryDataTypesNames {
    dataCategory: string;
    dataTypes: string[];
}
